import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type Currency,
  type Market,
  type PlusStatus,
  type User,
} from '../dataProvider/types';

/**
 * The Subscriptions query/aggregation module - the ONE place the Plus band
 * figures are computed. Same seam idiom as earningsModel / storePerformanceModel:
 * v1 aggregates client-side from the mock provider, every selector lives here, and
 * the late real-data migration swaps this module for a server-side endpoint
 * without the surface changing.
 *
 * THE MODEL (D-subscriptions). Plus is a prepaid period funded from the member's
 * WALLET balance (a fixed cashback amount per period), auto-renewed by debiting
 * the wallet again at period end. There is NO Stripe, NO card, NO payment
 * processor anywhere. Revenue is recognised at the wallet debit, so MRR is an
 * honest count of running subscriptions times the period price, never a modelled
 * or churn-adjusted projection.
 *
 * The surface is a FILTERED VIEW over users (plusStatus != none), not a new
 * resource, so subscription state reads off the User row and the audit floor and
 * Phase 2 trust line cover its two actions for free.
 */

// --- tunables (flagged for review) -----------------------------------------

/**
 * The Plus period price, per market, in that market's currency (the wallet debit
 * per period and the per-subscription MRR contribution). uk is GBP 5. us is a
 * PLACEHOLDER round-dollar guess, NOT a converted 5: set the real figure.
 * [REVIEW: PERIOD_PRICE]
 */
export const PERIOD_PRICE: Record<Market, number> = {
  uk: 5, // GBP 5
  us: 7, // USD 7 [REVIEW placeholder - set the real US price, not a converted 5]
  ie: 5, // reserved market, not seeded
};

/**
 * The length of a comped Plus period, in days: a grant confers one period ending
 * this far ahead of now. [REVIEW: PLUS_PERIOD_DAYS]
 */
export const PLUS_PERIOD_DAYS = 30;

// --- types ------------------------------------------------------------------

/** One member's Plus subscription, as the surface renders it. */
export interface SubscriptionRow {
  id: number;
  name: string;
  email: string;
  market: Market;
  plusStatus: PlusStatus;
  autoRenew: boolean;
  currentPeriodEnd: string | null;
  billingReference: string | null;
}

export interface SubscriptionsBand {
  market: Market;
  currency: Currency;
  /** The per-period price for this market (MRR = activeCount x periodPrice). */
  periodPrice: number;
  /** Honest MRR: active subscriptions x periodPrice. Excludes every other state. */
  mrr: number;
  activeCount: number;
  /** The at-risk figure: still in a paid period but auto-renew off. NOT in MRR. */
  pendingCancelCount: number;
  lapsedCount: number;
  /** Renewal failed for funds: DISTINCT from lapsed, the recoverable group. */
  lapsedInsufficientCount: number;
  /** Members with plusStatus != none in this market (the leaderboard rows). */
  members: SubscriptionRow[];
}

export interface SubscriptionsView {
  bands: SubscriptionsBand[];
}

export interface SubscriptionsInput {
  users: User[];
}

export interface SubscriptionsOptions {
  /** Access scope (UK | US | GLOBAL); decides which market bands render. */
  scope: string;
}

// --- helpers ----------------------------------------------------------------

/** The market bands a scope renders: scope -> markets, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

// Members sort by state (the running ones first, then the ended ones), then name,
// so the actionable rows sit at the top of each band.
const STATE_ORDER: Record<PlusStatus, number> = {
  active: 0,
  pending_cancel: 1,
  lapsed_insufficient: 2,
  lapsed: 3,
  none: 4,
};

// --- aggregation ------------------------------------------------------------

/** Build the full view for the active scope (one band per derived market). */
export function buildSubscriptionsView(
  input: SubscriptionsInput,
  opts: SubscriptionsOptions,
): SubscriptionsView {
  const bands = bandsForScope(opts.scope).map((market) =>
    buildBand(market, input),
  );
  return { bands };
}

function buildBand(
  market: Market,
  input: SubscriptionsInput,
): SubscriptionsBand {
  // Plus members in this market (the filtered view excludes plusStatus none).
  const members: SubscriptionRow[] = input.users
    .filter((u) => u.market === market && u.plusStatus !== 'none')
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      market: u.market,
      plusStatus: u.plusStatus,
      autoRenew: u.autoRenew,
      currentPeriodEnd: u.currentPeriodEnd,
      billingReference: u.billingReference,
    }))
    .sort((a, b) => {
      const order = STATE_ORDER[a.plusStatus] - STATE_ORDER[b.plusStatus];
      return order !== 0 ? order : a.name.localeCompare(b.name);
    });

  const countOf = (status: PlusStatus): number =>
    members.filter((m) => m.plusStatus === status).length;

  const activeCount = countOf('active');
  const periodPrice = PERIOD_PRICE[market];

  return {
    market,
    currency: CURRENCY_BY_MARKET[market],
    periodPrice,
    // Honest MRR: count x price, active only. Never blended with pending/lapsed.
    mrr: activeCount * periodPrice,
    activeCount,
    pendingCancelCount: countOf('pending_cancel'),
    lapsedCount: countOf('lapsed'),
    lapsedInsufficientCount: countOf('lapsed_insufficient'),
    members,
  };
}
